import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { Loader2 } from "lucide-react";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
  }, []);

  return <AuthContext.Provider value={{ user, setUser, logout, checkAuth }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function FullScreenLoader({ label = "Loading SHIPANY" }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-shipany-mist gap-3" data-testid="fullscreen-loader">
      <Loader2 className="h-8 w-8 animate-spin text-shipany-blue" />
      <p className="text-sm text-slate-500 font-medium">{label}…</p>
    </div>
  );
}

export function ProtectedRoute({ children, admin = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  const staff = ["admin", "super_admin", "operations", "support", "finance", "warehouse_manager"].includes(user.role);
  if (admin && !staff) return <Navigate to={user.role === "franchise" ? "/franchise" : "/app"} replace />;
  if (!admin && staff && location.pathname === "/app") return <Navigate to="/admin" replace />;
  if (!admin && user.role === "franchise" && location.pathname.startsWith("/app")) return <Navigate to="/franchise" replace />;
  // KYC-first onboarding: unapproved customers are locked to the KYC wizard until admin approves.
  // Franchise accounts have their own KYC gate inside FranchiseLayout.
  if (!admin && !staff && user.role !== "franchise" && user.kyc_status !== "approved" && location.pathname !== "/app/kyc") {
    return <Navigate to="/app/kyc" replace />;
  }
  return children;
}

